// Third Party
import { useState, useMemo } from "react";

// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
// helpeers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import NotPermission from "@/components/NotPermission";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { DataTable } from "@/components/ui/data-table";
import { columns } from "@/components/columns/LawColumns";
import Pagination from "@/components/Pagination";
import TableSkeleton from "@/components/skeleton/TableSkeleton";

// Icons

// Services
import { getLaws } from "@/services/law";
import { getLawTypes } from "@/services/master";
import { ErrorResponse } from "@/types/ErrorResponse";
import type { AxiosError } from "axios";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
import type { Law } from "@/types/law";

interface LawType {
  id: string;
  name: string;
}

interface LawTypesResponse {
  status: boolean;
  message: string;
  data: LawType[];
}

interface LawsResponse {
  status: boolean;
  message: string;
  total: number;
  data: Law[];
}

export const Route = createFileRoute("/_layout-private/law/")({
  component: LawComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
      search: (search.search as string) || "",
      lawTypeId: (search.lawTypeId as string) || "",
      createdAt: (search.createdAt as string) || "",
      startYear: Number(search.startYear) || 0,
      endYear: Number(search.endYear) || 0,
    };
  },
});

function LawComponent() {
  // Instance
  const navigate = useNavigate();
  const { lawManagement } = useAccessPermission();
  const {
    page,
    search: searchParam,
    lawTypeId: lawTypeIdParam,
    startYear: startYearParam,
    endYear: endYearParam,
  } = Route.useSearch();

  const initialZero = 0;
  const currentYear = new Date().getFullYear();

  const [filter, setFilter] = useState({
    search: searchParam,
    lawTypeId: lawTypeIdParam,
    skip: (page - 1) * 40,
    limit: 40,
    updatedAt: undefined,
    startYear: startYearParam || initialZero,
    endYear: endYearParam || initialZero,
  });

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!filter.startYear || filter.startYear === initialZero) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === filter.startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [filter.startYear, yearOptions]);

  const [debounceSearch] = useDebouncedValue(filter.search, 500);

  // Query
  const {
    data: laws,
    isLoading,
    error: lawsError,
  } = useQuery<LawsResponse, AxiosError<ErrorResponse>>({
    queryKey: [
      "laws",
      debounceSearch,
      filter.lawTypeId,
      page,

      filter.startYear,
      filter.endYear,
    ],
    queryFn: () => {
      // Constructing request params
      const params: { [key: string]: string | number } = {
        skip: (page - 1) * 40,
        limit: 40,
      };

      if (debounceSearch.trim()) {
        params.search = debounceSearch;
      }

      if (filter.lawTypeId && filter.lawTypeId !== " ") {
        params.lawTypeId = filter.lawTypeId;
      }

      if (filter.startYear !== initialZero) {
        params.startYear = filter.startYear;
      }

      if (filter.endYear !== initialZero) {
        params.endYear = filter.endYear;
      }

      return getLaws(params);
    },
  });
  const { data: lawTypesResponse, error } = useQuery<
    LawTypesResponse,
    AxiosError<ErrorResponse>
  >({
    queryKey: ["lawTypes"],
    queryFn: () => getLawTypes(),
  });

  if (error || lawsError) {
    if (
      error?.response?.data?.message === "LOGGED_OUT" ||
      lawsError?.response?.data?.message === "LOGGED_OUT"
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Functions
  const handlePageClick = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page,
      }),
    } as NavigateOptions);
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    setFilter((prev) => ({ ...prev, search: trimmedValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        search: trimmedValue || undefined,
        page: 1,
      }),
    } as NavigateOptions);
  };

  const handleLawTypeChange = (value: string) => {
    const trimmedValue = value.trim();
    setFilter((prev) => ({ ...prev, lawTypeId: trimmedValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        lawTypeId: trimmedValue || undefined,
        page: 1,
      }),
    } as NavigateOptions);
  };

  const handleStartYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, startYear: yearValue }));

    if (filter.endYear !== initialZero && filter.endYear < yearValue) {
      setFilter((prev) => ({ ...prev, endYear: yearValue }));
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          endYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    }
  };

  const handleEndYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, endYear: yearValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        endYear: yearValue,
        page: 1,
      }),
    } as NavigateOptions);
  };

  const lawTypes = lawTypesResponse?.data;
  const data = laws?.data;

  return lawManagement.isRead ? (
    <section>
      <p className="text-[14px] mb-[32px]">ຈັດການກົດໝາຍ ແລະ ນິຕິກຳ</p>

      <Card>
        <CardHeader>
          <CardTitle>ກົດໝາຍ ແລະ ນິຕິກຳ</CardTitle>
          <CardDescription>ຕາຕະລາງກົດໝາຍ ແລະ ນິຕິກຳທັງໝົດ</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between">
            <div className="flex items-end gap-[8px] flex-wrap">
              <div>
                <Label htmlFor="search">ຄົ້ນຫາ</Label>
                <Input
                  id="search"
                  placeholder="ຄົ້ນຫາຂ່າວສານ..."
                  className="w-[350px]"
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
                <Select
                  value={filter.startYear.toString()}
                  onValueChange={handleStartYearChange}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">ທັງໝົດ</SelectItem>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="">ປີສິ້ນສຸດ</Label>
                <Select
                  value={filter.endYear.toString()}
                  onValueChange={handleEndYearChange}
                  disabled={
                    !filter.startYear || filter.startYear === initialZero
                  }
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    {endYearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* <DatePicker
								label="ສະແດງຕາມວັນທີອັບເດດລ່າສຸດ"
								name="date"
								id="date"
								value={filterDate || ""}
								onChange={handleDateChange}
							/>
							{filterDate && (
								<Button variant="outline" size="icon" onClick={handleClearDate}>
									<X className="w-4 h-4" />
								</Button>
							)} */}
              <div>
                <Label htmlFor="infoTypeId">ສະແດງຕາມປະເພດກົດໝາຍ</Label>
                <Select
                  name="infoTypeId"
                  value={filter.lawTypeId}
                  onValueChange={handleLawTypeChange}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="ເລືອກປະເພດກົດໝາຍ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key={""} value={" "}>
                      ທັງໝົດ
                    </SelectItem>
                    {lawTypes?.map((infoType: { id: string; name: string }) => (
                      <SelectItem key={infoType.id} value={infoType.id}>
                        {infoType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <div className="my-[16px]">
              <DataTable
                columns={columns(lawManagement.isDownload)}
                data={data || []}
                onRowClick={(row) =>
                  navigate({
                    to: `/law/${row.id}`,
                    search: {
                      data: JSON.stringify(row),
                    },
                  })
                }
              />
              <Pagination
                handlePageClick={handlePageClick}
                totalItems={laws?.total || 0}
                itemsPerPage={40}
                currentPage={page}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  ) : (
    <NotPermission />
  );
}
